
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

async function getCurrentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

export interface VisionImage {
  id: string;
  name: string;
  url: string;
  isAIGenerated: boolean;
  createdAt: string;
  prompt?: string;
  type?: "image" | "video";
}

export type ReferenceKind = "person" | "place" | "object";

export interface ReferencePhoto {
  id: string;
  label: string;
  url: string;
  kind: ReferenceKind;
}

export function useVisionBoard() {
  const [userId, setUserId] = useState<string | null>(null);
  const [images, setImages] = useState<VisionImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAssisting, setIsAssisting] = useState(false);
  const [referencePhotos, setReferencePhotos] = useState<ReferencePhoto[]>([]);

  // Load user ID and data on mount
  useEffect(() => {
    getCurrentUserId().then((uid) => {
      if (uid) {
        setUserId(uid);
        loadFromDatabase(uid);
      }
    });
  }, []);

  const loadFromDatabase = async (uid: string) => {
    // Load vision board items
    const { data: items, error: itemsError } = await supabase
      .from("vision_board_items")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: true });

    if (!itemsError && items) {
      setImages(items.map((item: any) => ({
        id: item.id,
        name: item.name,
        url: item.url,
        isAIGenerated: item.is_ai_generated,
        createdAt: item.created_at,
        prompt: item.prompt,
        type: item.type as "image" | "video" | undefined,
      })));
    }

    // Migrate from localStorage if DB is empty
    if ((!items || items.length === 0) && !itemsError) {
      const localKey = `vision-board-images-${uid}`;
      const stored = localStorage.getItem(localKey);
      if (stored) {
        try {
          const localImages: VisionImage[] = JSON.parse(stored);
          if (localImages.length > 0) {
            const rows = localImages.map((img) => ({
              id: img.id,
              user_id: uid,
              name: img.name,
              url: img.url,
              is_ai_generated: img.isAIGenerated,
              prompt: img.prompt || null,
              type: img.type || "image",
              created_at: img.createdAt,
            }));
            await supabase.from("vision_board_items").insert(rows);
            setImages(localImages);
            localStorage.removeItem(localKey);
          }
        } catch { /* ignore parse errors */ }
      }
    }

    // Load reference photos
    const { data: refs, error: refsError } = await supabase
      .from("reference_photos")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: true });

    if (!refsError && refs) {
      setReferencePhotos(refs.map((r: any) => ({
        id: r.id,
        label: r.label,
        url: r.url,
        kind: (r.kind as ReferenceKind) || "person",
      })));
    }

    // Migrate reference photos from localStorage
    if ((!refs || refs.length === 0) && !refsError) {
      const refKey = `vision-board-reference-photos-${uid}`;
      const stored = localStorage.getItem(refKey);
      if (stored) {
        try {
          const localRefs: ReferencePhoto[] = JSON.parse(stored);
          if (localRefs.length > 0) {
            const rows = localRefs.map((r) => ({
              id: r.id,
              user_id: uid,
              label: r.label,
              url: r.url,
              kind: r.kind || "person",
            }));
            await supabase.from("reference_photos").insert(rows);
            setReferencePhotos(localRefs.map((r) => ({ ...r, kind: r.kind || "person" })));
            localStorage.removeItem(refKey);
          }
        } catch { /* ignore */ }
      }
    }
  };

  const uploadImages = useCallback(async (files: File[]) => {
    setIsUploading(true);
    const newImages: VisionImage[] = [];
    try {
      const uid = await getCurrentUserId();
      if (!uid) throw new Error("Not authenticated");

      for (const file of files) {
        const fileName = `${uid}/uploads/${crypto.randomUUID()}-${file.name}`;
        const { error } = await supabase.storage
          .from("vision-board")
          .upload(fileName, file, { contentType: file.type });

        if (error) {
          console.error("Upload error:", error);
          toast({ title: "Upload failed", description: `Could not upload ${file.name}`, variant: "destructive" });
          continue;
        }

        const { data: publicUrlData } = supabase.storage
          .from("vision-board")
          .getPublicUrl(fileName);

        const isVideo = file.type.startsWith("video/");
        const newItem: VisionImage = {
          id: crypto.randomUUID(),
          name: file.name,
          url: publicUrlData.publicUrl,
          isAIGenerated: false,
          createdAt: new Date().toISOString(),
          ...(isVideo ? { type: "video" as const } : {}),
        };

        // Save to database
        await supabase.from("vision_board_items").insert({
          id: newItem.id,
          user_id: uid,
          name: newItem.name,
          url: newItem.url,
          is_ai_generated: false,
          type: isVideo ? "video" : "image",
          created_at: newItem.createdAt,
        });

        newImages.push(newItem);
      }

      if (newImages.length > 0) {
        setImages((prev) => [...prev, ...newImages]);
        toast({ title: "Uploaded!", description: `${newImages.length} image(s) added to your vision board.` });
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Upload error", description: "Something went wrong.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  }, []);

  const addReferencePhoto = useCallback(async (file: File, label: string, kind: ReferenceKind = "person") => {
    setIsUploading(true);
    try {
      const uid = await getCurrentUserId();
      if (!uid) throw new Error("Not authenticated");

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const fileName = `${uid}/reference/${crypto.randomUUID()}-${safeName}`;
      const { error } = await supabase.storage
        .from("vision-board")
        .upload(fileName, file, { contentType: file.type });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from("vision-board")
        .getPublicUrl(fileName);

      const newRef: ReferencePhoto = {
        id: crypto.randomUUID(),
        label: label.trim() || "Reference",
        url: publicUrlData.publicUrl,
        kind,
      };

      await supabase.from("reference_photos").insert({
        id: newRef.id,
        user_id: uid,
        label: newRef.label,
        url: newRef.url,
        kind: newRef.kind,
      });

      setReferencePhotos((prev) => [...prev, newRef]);
      toast({ title: "Reference added!", description: `"${newRef.label}" will be available for AI generations.` });
      return newRef;
    } catch (e) {
      console.error(e);
      toast({ title: "Upload failed", description: "Could not upload reference photo.", variant: "destructive" });
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const updateReferenceLabel = useCallback(async (id: string, label: string) => {
    const newLabel = label.trim();
    if (!newLabel) return;
    setReferencePhotos((prev) =>
      prev.map((ref) => (ref.id === id ? { ...ref, label: newLabel } : ref))
    );
    await supabase.from("reference_photos").update({ label: newLabel }).eq("id", id);
  }, []);

  const updateReferenceKind = useCallback(async (id: string, kind: ReferenceKind) => {
    setReferencePhotos((prev) =>
      prev.map((ref) => (ref.id === id ? { ...ref, kind } : ref))
    );
    await supabase.from("reference_photos").update({ kind }).eq("id", id);
  }, []);

  const removeReferencePhoto = useCallback(async (id: string) => {
    setReferencePhotos((prev) => prev.filter((ref) => ref.id !== id));
    await supabase.from("reference_photos").delete().eq("id", id);
    toast({ title: "Reference removed" });
  }, []);

  const generateImage = useCallback(async (prompt: string, selectedRefs: { url: string; label: string; kind: ReferenceKind }[] = []) => {
    setIsGenerating(true);
    try {
      const uid = await getCurrentUserId();
      if (!uid) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("generate-vision-image", {
        body: {
          prompt,
          references: selectedRefs,
          // legacy field kept for backward compatibility
          referenceImageUrls: selectedRefs.map((r) => r.url),
          userId: uid,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const newImage: VisionImage = {
        id: crypto.randomUUID(),
        name: prompt.slice(0, 50),
        url: data.url,
        isAIGenerated: true,
        createdAt: new Date().toISOString(),
        prompt,
      };

      await supabase.from("vision_board_items").insert({
        id: newImage.id,
        user_id: uid,
        name: newImage.name,
        url: newImage.url,
        is_ai_generated: true,
        prompt: newImage.prompt,
        type: "image",
        created_at: newImage.createdAt,
      });

      setImages((prev) => [...prev, newImage]);
      toast({ title: "Image generated!", description: "AI image added to your vision board." });
      return newImage;
    } catch (e: any) {
      console.error(e);
      toast({ title: "Generation failed", description: e.message || "Could not generate image.", variant: "destructive" });
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const assistPrompt = useCallback(async (prompt: string): Promise<string | null> => {
    setIsAssisting(true);
    try {
      const { data, error } = await supabase.functions.invoke("assist-vision-prompt", {
        body: { prompt },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data.improvedPrompt || null;
    } catch (e: any) {
      console.error(e);
      toast({ title: "Assist failed", description: e.message || "Could not improve prompt.", variant: "destructive" });
      return null;
    } finally {
      setIsAssisting(false);
    }
  }, []);

  const removeImage = useCallback(async (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
    await supabase.from("vision_board_items").delete().eq("id", id);
    toast({ title: "Removed", description: "Image removed from your vision board." });
  }, []);

  const expandImage = useCallback(async (id: string, imageUrl: string, name: string) => {
    setIsGenerating(true);
    try {
      const uid = await getCurrentUserId();
      if (!uid) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("expand-vision-image", {
        body: { imageUrl, userId: uid },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const expanded: VisionImage = {
        id: crypto.randomUUID(),
        name: `${name} (HD)`,
        url: data.url,
        isAIGenerated: true,
        createdAt: new Date().toISOString(),
        prompt: `Expanded to 1920×1080: ${name}`,
      };

      await supabase.from("vision_board_items").insert({
        id: expanded.id,
        user_id: uid,
        name: expanded.name,
        url: expanded.url,
        is_ai_generated: true,
        prompt: expanded.prompt,
        type: "image",
        created_at: expanded.createdAt,
      });

      setImages((prev) => [...prev, expanded]);
      toast({ title: "Expanded to HD!", description: "1920×1080 version added to your vision board." });
      return expanded;
    } catch (e: any) {
      console.error(e);
      toast({ title: "Expansion failed", description: e.message || "Could not expand image.", variant: "destructive" });
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const upscaleImage = useCallback(async (id: string, imageUrl: string, name: string) => {
    setIsGenerating(true);
    try {
      const uid = await getCurrentUserId();
      if (!uid) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("upscale-vision-image", {
        body: { imageUrl, userId: uid },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const upscaled: VisionImage = {
        id: crypto.randomUUID(),
        name: `${name} (Enhanced)`,
        url: data.url,
        isAIGenerated: true,
        createdAt: new Date().toISOString(),
        prompt: `Upscaled & enhanced: ${name}`,
      };

      await supabase.from("vision_board_items").insert({
        id: upscaled.id,
        user_id: uid,
        name: upscaled.name,
        url: upscaled.url,
        is_ai_generated: true,
        prompt: upscaled.prompt,
        type: "image",
        created_at: upscaled.createdAt,
      });

      setImages((prev) => [...prev, upscaled]);
      toast({ title: "Image enhanced!", description: "Upscaled version with face correction added to your board." });
      return upscaled;
    } catch (e: any) {
      console.error(e);
      toast({ title: "Upscale failed", description: e.message || "Could not upscale image.", variant: "destructive" });
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const saveVideo = useCallback(async (videoUrl: string, sourceName: string) => {
    const uid = await getCurrentUserId();
    const newItem: VisionImage = {
      id: crypto.randomUUID(),
      name: `${sourceName} (video)`,
      url: videoUrl,
      isAIGenerated: true,
      createdAt: new Date().toISOString(),
      type: "video",
    };

    if (uid) {
      await supabase.from("vision_board_items").insert({
        id: newItem.id,
        user_id: uid,
        name: newItem.name,
        url: newItem.url,
        is_ai_generated: true,
        type: "video",
        created_at: newItem.createdAt,
      });
    }

    setImages((prev) => [...prev, newItem]);
    toast({ title: "Video saved!", description: "Animated clip added to your Vision Board." });
    return newItem;
  }, []);

  return {
    images, isUploading, isGenerating, isAssisting,
    referencePhotos,
    addReferencePhoto, updateReferenceLabel, updateReferenceKind, removeReferencePhoto,
    generateImage, assistPrompt, removeImage, uploadImages, saveVideo, expandImage, upscaleImage,
    referenceImageUrl: referencePhotos[0]?.url ?? null,
  };
}
